import { useTranslation } from "react-i18next";

import { LANGUAGES, LANGUAGE_NAMES, setLanguage } from "../i18n/index.js";

/**
 * The language picker.
 *
 * A row of buttons rather than a `<select>`, because there are two languages and
 * a dropdown to choose between two things is a click somebody has to spend to
 * find out what their options were. It becomes a `<select>` when the list stops
 * fitting — that is a real threshold, not a style preference.
 *
 * # Each language names itself
 *
 * "Español", never "Spanish" — somebody looking for their own language is
 * looking for the word they call it, and they are by definition reading a page
 * in a language they may not have. This is why the names are not in the
 * translation files at all.
 */
export function LanguageSwitch({ className }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const current = i18n.resolvedLanguage;

  return (
    <div className={className} role="group" aria-label={t("language.change")}>
      {LANGUAGES.map((language) => {
        const active = current === language;
        return (
          <button
            key={language}
            type="button"
            // The state is announced, not just coloured — the same rule the
            // board follows.
            aria-current={active ? "true" : undefined}
            onClick={() => {
              void setLanguage(language);
            }}
            className={
              active
                ? "rounded-md px-2.5 py-1 text-xs font-semibold text-stage-950 bg-flash-500"
                : "rounded-md px-2.5 py-1 text-xs font-medium text-white/55 transition hover:bg-white/10 hover:text-white"
            }
          >
            {LANGUAGE_NAMES[language]}
          </button>
        );
      })}
    </div>
  );
}
